import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "korkman2_auth_token";

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
  }
}

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/** Indholdet af login-tokenet (sub, role, iat, exp) - eller {} (2026-10-25). */
export function tokenClaims(token = getAuthToken()) {
  try {
    const part = String(token || "").split(".")[1] || "";
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")));
  } catch (_) { return {}; }
}
const tokenExpired = () => { const e = tokenClaims().exp; return !!e && e * 1000 <= Date.now(); };

// Apply token on module load (page refresh)
const _existing = getAuthToken();
if (_existing) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${_existing}`;
}

// 2026-10-25 (sikkerhed): Serveren kan tilbagekalde et login (nyt kodeord,
// deaktiveret, 2FA krævet). Så ryddes login'et, og man sendes til login-siden
// med forklaringen - i stedet for at hver side viser en fejl.
export const LOGIN_REASON_KEY = "cf_login_reason";
axios.interceptors.response.use(undefined, (error) => {
  const r = error?.response;
  const code = r?.data?.code;
  // 2026-10-26: En fane, hvis login ikke længere gælder (fx fordi der er logget ind
  // eller ud i en anden fane), blev ved med at spørge serveren i det uendelige og
  // brugte kvoten op, så et nyt login blev afvist (429). Nu tager den det gemte login
  // i brug - eller går til login-siden, hvis dens eget login er ugyldigt.
  const sent = String(error?.config?.headers?.Authorization || error?.config?.headers?.authorization || "");
  const url = String(error?.config?.url || "");
  if (r?.status === 401 && !code && sent.startsWith("Bearer ") && !/\/auth\/(login|mfa|renew)/.test(url)) {
    const stored = getAuthToken();
    if (stored && sent !== `Bearer ${stored}`) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${stored}`;
      return Promise.reject(error);
    }
    const msg = tokenExpired() ? "Dit login er udløbet - log ind igen. Det, der venter på telefonen, sendes bagefter."
      : "Dit login gælder ikke længere - log ind igen.";
    setAuthToken(null);
    try { sessionStorage.setItem(LOGIN_REASON_KEY, msg); } catch (_) { /* ignore */ }
    window.location.assign("/");
    return Promise.reject(error);
  }
  // 2026-10-25 (S1): Også et udløbet login - chaufførens ventende handlinger bliver
  // på telefonen og sendes, når han har logget ind igen.
  const expired = r?.status === 401 && !code && tokenExpired();
  if (r?.status === 401 && (["session_revoked", "mfa_required", "mfa_setup_required"].includes(code) || expired) && getAuthToken()) {
    try { sessionStorage.setItem(LOGIN_REASON_KEY, expired ? "Dit login er udløbet - log ind igen. Det, der venter på telefonen, sendes bagefter." : (r.data.detail || "")); } catch (_) { /* ignore */ }
    setAuthToken(null);
    try { localStorage.removeItem("containerflow_last_user"); } catch (_) { /* ignore */ }
    window.location.assign("/");
  }
  return Promise.reject(error);
});

// "Husk denne enhed" efter 2FA (30 dage) - pr. e-mail.
const deviceKey = (email) => `cf_mfa_device:${(email || "").toLowerCase()}`;
export function getDeviceToken(email) { try { return localStorage.getItem(deviceKey(email)) || ""; } catch (_) { return ""; } }
export function setDeviceToken(email, t) { try { if (t) localStorage.setItem(deviceKey(email), t); else localStorage.removeItem(deviceKey(email)); } catch (_) { /* ignore */ } }

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Noget gik galt";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

const OFFLINE_MSG = "Ingen forbindelse til serveren. Prøv igen om lidt.";

export const authApi = {
  async login(email, password, companyId = "") {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, device_token: getDeviceToken(email), ...(companyId ? { company_id: companyId } : {}) });
    // 2026-10-30: Samme e-mail og kodeord i flere firmaer - personen vælger firmaet.
    if (data && Array.isArray(data.choose_company)) return data;
    // 2026-10-25: Næste trin er 2FA (kode eller opsætning).
    if (data && (data.mfa_required || data.mfa_setup_required) && data.mfa_token) return data;
    // 2026-10-22: Et svar uden bruger/token (fx fra en gammel offline-hjælper
    // når serveren ikke svarer) er ikke et login.
    if (!data || !data.token || !data.user) throw new Error(OFFLINE_MSG);
    return data;
  },
  async me() {
    const { data } = await axios.get(`${API}/auth/me`);
    if (!data || !data.id || !data.role) throw new Error(OFFLINE_MSG);
    return data;
  },
  async logout() {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_) { /* ignore */ }
  },
  async changePassword(oldPassword, newPassword) {
    const { data } = await axios.post(`${API}/auth/change-password`, {
      old_password: oldPassword,
      new_password: newPassword,
    });
    // 2026-10-25: Nyt kodeord logger alle andre logins ud - her et nyt token.
    if (data?.token) setAuthToken(data.token);
    return data;
  },
};
